import React from 'react';
import { Calendar, Info } from 'lucide-react';
import UserGuideModal, { TIER_NAMES, TIER_DESCRIPTIONS, MARKETING_BULLETS } from './UserGuideModal';

// Extracted from App.js (App.js modularization, Sep 2026) — the
// step === 'onboarding' screen: language/currency toggle, plan selector
// with its two nested sub-components (TierCard, AccordionGroup), and the
// "Coming Soon" dialog for paid tiers not yet available for online signup.
//
// No Settings button on this screen in the original, so no equivalent of
// the create-calendar/welcome dead-button fix was needed here.
const OnboardingStep = ({
  onboardingLang,
  setOnboardingLang,
  onboardingCurrency,
  setOnboardingCurrency,
  currencyManuallySet,
  setCurrencyManuallySet,
  onboardingTiersList,
  setOnboardingTiersList,
  tiersLoading,
  loadOnboardingTiers,
  showPlanSelector,
  setShowPlanSelector,
  activatingTier,
  activatingTierName,
  handleSelectTier,
  expandedTierGroup,
  setExpandedTierGroup,
  showComingSoon,
  setShowComingSoon,
  selectedPaidTier,
  setSelectedPaidTier,
  showUserGuide,
  onOpenUserGuide,
  onCloseUserGuide,
  onSignOut,
  userEmail,
  userPreferredCurrency,
}) => {
  const handleSetLang = (l) => {
    setOnboardingLang(l);
    // Auto-switch currency to EUR for FR unless user has manually overridden
    if (l === 'fr' && !currencyManuallySet) {
      setOnboardingCurrency('EUR');
      setOnboardingTiersList([]); // force reload with new currency
    }
  };

  const handleSetCurrency = (c) => {
    setOnboardingCurrency(c);
    setCurrencyManuallySet(true);
    setOnboardingTiersList([]); // force reload with new currency
  };

  // Load tiers when plan selector is opened, or when currency has been cleared
  const handleOpenPlanSelector = () => {
    setShowPlanSelector(true);
    if (onboardingTiersList.length === 0) loadOnboardingTiers(onboardingCurrency);
  };

  // Reload tiers if currency changed while plan selector is already open
  if (showPlanSelector && onboardingTiersList.length === 0 && !tiersLoading) {
    loadOnboardingTiers(onboardingCurrency);
  }

  const communityTiers = onboardingTiersList.filter(t => t.user_type === 'community');
  const enterpriseTiers = onboardingTiersList.filter(t => t.user_type === 'enterprise');

  const currencySymbol = { GBP: '£', EUR: '€', USD: '$' }[onboardingCurrency] || '£';
  const isFr = onboardingLang === 'fr';

  const TierCard = ({ tier }) => {
    const bullets = (MARKETING_BULLETS[onboardingLang] || MARKETING_BULLETS.en)[tier.tier_name] || [];
    const displayName = (TIER_NAMES[onboardingLang] || TIER_NAMES.en)[tier.tier_name] || tier.display_name;
    const description = (TIER_DESCRIPTIONS[onboardingLang] || TIER_DESCRIPTIONS.en)[tier.tier_name] || tier.description;
    const isActivatingThis = activatingTierName === tier.tier_name;
    return (
      <div className={`border rounded-lg p-4 mb-2 transition ${isActivatingThis ? 'border-blue-400 bg-blue-50 cursor-wait' : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50 cursor-pointer'}`}
        onClick={() => !activatingTier && handleSelectTier(tier)}>
        {isActivatingThis ? (
          <div className="flex items-center justify-center gap-2 py-2 text-blue-600 text-sm font-medium">
            <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
            {isFr ? 'Activation de votre formule…' : 'Activating your plan...'}
          </div>
        ) : (
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="font-semibold text-gray-800 text-sm">{displayName}</div>
              <div className="text-xs text-gray-500 mt-0.5">{description}</div>
              {bullets.length > 0 && (
                <ul className="mt-2 space-y-0.5">
                  {bullets.map((f, i) => (
                    <li key={i} className="text-xs text-gray-500 flex items-start gap-1">
                      <span className="text-blue-400 mt-0.5">•</span>{f}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="text-right flex-shrink-0 pt-0.5">
              {tier.price ? (
                <div>
                  <span className="font-bold text-gray-800">{currencySymbol}{tier.price}</span>
                  <span className="text-xs text-gray-500">/year</span>
                </div>
              ) : (
                <span className="text-green-600 font-bold text-sm">{isFr ? 'Gratuit' : 'Free'}</span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const AccordionGroup = ({ title, tiers, groupKey, badge }) => (
    <div className="border border-gray-200 rounded-xl mb-4 overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-5 py-4 bg-white hover:bg-gray-50 transition"
        onClick={() => setExpandedTierGroup(expandedTierGroup === groupKey ? null : groupKey)}
      >
        <div className="flex items-center gap-3">
          <span className="font-bold text-gray-800">{title}</span>
          {badge && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{badge}</span>}
        </div>
        <span className="text-gray-400 text-lg">{expandedTierGroup === groupKey ? '▲' : '▼'}</span>
      </button>
      {expandedTierGroup === groupKey && (
        <div className="px-4 pb-4 pt-2 bg-gray-50">
          {tiers.map(tier => <TierCard key={tier.tier_name} tier={tier} />)}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* HEADER */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <h1 className="text-xl font-bold text-gray-800">Telematrix Calendar Hub</h1>
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              {/* Language toggle */}
              <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
                <button onClick={() => handleSetLang('en')} className={`px-2 py-1.5 transition ${onboardingLang === 'en' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>EN</button>
                <button onClick={() => handleSetLang('fr')} className={`px-2 py-1.5 border-l border-gray-200 transition ${onboardingLang === 'fr' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>FR</button>
              </div>
              {/* Currency selector */}
              <select value={onboardingCurrency} onChange={(e) => handleSetCurrency(e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="GBP">£ GBP</option>
                <option value="EUR">€ EUR</option>
                <option value="USD">$ USD</option>
              </select>
              <button onClick={onOpenUserGuide} className="flex items-center gap-1 text-gray-600 hover:text-blue-600 px-2 py-1.5 text-xs font-medium transition">
                <Info className="w-4 h-4" />
                {isFr ? 'Guide' : 'Guide'}
              </button>
              <button onClick={onSignOut} className="px-2 py-1.5 text-xs text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition">
                {isFr ? 'Déconnexion' : 'Sign Out'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">

        {/* Welcome banner */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6 text-center">
          <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">{isFr ? 'Bienvenue sur Calendar Hub' : 'Welcome to Calendar Hub'}</h2>
          <p className="text-gray-500 text-sm">{isFr ? 'Connecté en tant que' : 'You\'re signed in as'} <span className="font-medium text-gray-700">{userEmail}</span></p>
        </div>

        {/* How it works */}
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-5 mb-6 text-white">
          <h3 className="font-bold text-base mb-3">✨ {isFr ? 'Comment ça marche' : 'How it works'}</h3>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2"><span>✅</span><span>{isFr ? 'Créez et gérez des événements dans votre calendrier' : 'Create and manage events in your calendar'}</span></li>
            <li className="flex items-start gap-2"><span>✅</span><span>{isFr ? 'Les utilisateurs s\'abonnent via un lien simple — aucune application à télécharger' : 'Users subscribe via a simple link — no app download needed'}</span></li>
            <li className="flex items-start gap-2"><span>✅</span><span>{isFr ? 'Les mises à jour se synchronisent automatiquement — aucune intervention requise' : 'Calendar updates sync automatically — no user intervention required'}</span></li>
          </ul>
          <p className="text-xs text-blue-100 mt-3 italic">{isFr ? 'Ouvrez le Guide ci-dessus pour tous les détails.' : 'Open the Guide above for full details on how Calendar Hub works.'}</p>
        </div>

        {/* Plan selector */}
        {!showPlanSelector ? (
          <button
            onClick={handleOpenPlanSelector}
            className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-blue-700 transition shadow-md"
          >
            {isFr ? 'Choisir une formule' : 'Choose a Plan'}
          </button>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-1">{isFr ? 'Choisissez votre formule' : 'Choose your plan'}</h3>
            <p className="text-sm text-gray-500 mb-5">{isFr ? 'Sélectionnez une formule pour commencer. Les formules gratuites sont activées immédiatement.' : 'Select a plan to get started. Free plans activate immediately.'}</p>

            {tiersLoading ? (
              <div className="text-center py-6 text-gray-400 text-sm">{isFr ? 'Chargement des formules…' : 'Loading plans...'}</div>
            ) : (
              <>
                <AccordionGroup
                  title="Community"
                  tiers={communityTiers}
                  groupKey="community"
                  badge={isFr ? 'Options gratuites disponibles' : 'Free options available'}
                />
                <AccordionGroup
                  title="Enterprise"
                  tiers={enterpriseTiers}
                  groupKey="enterprise"
                  badge={isFr ? 'Formules annuelles' : 'Annual plans'}
                />
              </>
            )}


          </div>
        )}

      </div>

      {/* Coming Soon dialog for paid tiers */}
      {showComingSoon && selectedPaidTier && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full text-center">
            <div className="text-4xl mb-4">🚀</div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">{isFr ? 'Bientôt disponible' : 'Coming Soon'}</h3>
            <p className="text-gray-600 text-sm mb-2">
              <span className="font-semibold">{(TIER_NAMES[onboardingLang] || TIER_NAMES.en)[selectedPaidTier.tier_name] || selectedPaidTier.display_name}</span> {isFr ? 'n\'est pas encore disponible en ligne.' : 'is not yet available for online signup.'}
            </p>
            <p className="text-gray-500 text-sm mb-6">
              {isFr ? 'Veuillez nous contacter via la page Contact sur' : 'Please get in touch via the Contact page at'}{' '}
              <a href="https://www.telematrixconsulting.com" target="_blank" rel="noreferrer" className="text-blue-600 underline">
                www.telematrixconsulting.com
              </a>{' '}
              to arrange access.
            </p>
            <button
              onClick={() => { setShowComingSoon(false); setSelectedPaidTier(null); }}
              className="w-full bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 transition"
            >
              {isFr ? 'OK' : 'OK'}
            </button>
          </div>
        </div>
      )}

      <UserGuideModal
        isOpen={showUserGuide}
        onClose={onCloseUserGuide}
        isOnDashboard={false}
        userPreferredCurrency={userPreferredCurrency}
      />
    </div>
  );
};

export default OnboardingStep;
